import { addEntries, type Entry, type Level } from './store.ts';

/**
 * The app's diagnostics logger (ADR-0024). Cheap to call anywhere: entries go to an in-memory queue and are written in
 * one batch about once a second (and on pagehide), so logging never adds an IndexedDB write per event and a frozen
 * page never blocks on storage. Data is numbers, booleans and short codes only: no notes, names, email or free text.
 */
export const SID = Math.random().toString(36).slice(2, 10);
const queue: Entry[] = [];
/** The latest entries in memory, for the debug panel and the error screen. */
export const recentEntries: Entry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

type Data = Record<string, unknown>;
const CODE = /^[\w .:/@+#-]{0,80}$/;
function clean(data?: Data, free = false): Entry['data'] {
  if (!data) return undefined;
  const out: NonNullable<Entry['data']> = {};
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === 'number') out[k] = Number.isFinite(v) ? Math.round(v * 100) / 100 : null;
    else if (typeof v === 'boolean' || v === null) out[k] = v;
    else if (typeof v === 'string') out[k] = free ? v.slice(0, 300) : CODE.test(v) ? v : '[text]';
  }
  return out;
}

export function diag(area: string, event: string, data?: Data, level: Level = 'info'): void {
  const d = clean(data, level === 'error');
  const e: Entry = { t: Date.now(), sid: SID, level, area, event, ...(d ? { data: d } : {}) };
  queue.push(e);
  recentEntries.push(e);
  if (recentEntries.length > 200) recentEntries.shift();
  if (queue.length >= 100) void flush();
  else timer ??= setTimeout(() => void flush(), 1000);
}

export async function flush(): Promise<void> {
  if (timer) clearTimeout(timer);
  timer = null;
  const batch = queue.splice(0);
  await addEntries(batch);
}

/** Time a synchronous step; log it when it's slow enough to drop frames on a phone. */
export function timed<T>(area: string, event: string, fn: () => T, data?: Data, slowMs = 50): T {
  const s = performance.now();
  const r = fn();
  const ms = performance.now() - s;
  if (ms >= slowMs) diag('perf', `${area}.${event}`, { ms, ...data }, 'warn');
  return r;
}

export function logError(where: string, err: unknown): void {
  const e = err instanceof Error ? err : new Error(String(err));
  diag('error', where, { message: e.message, stack: (e.stack ?? '').split('\n').slice(0, 6).join(' | ') }, 'error');
}
